// The blog manifest, built from the .mdx files in src/content/blog at build time.
//
//   - `frontmatters` eager-imports only the `frontmatter` export of each article
//     (exported by remark-mdx-frontmatter) → the index has metadata synchronously.
//   - `loaders` lazily imports the compiled module for the detail route.
//
// NOTE: because the eager frontmatter import statically references each module,
// the bodies are not split into separate chunks (Vite logs an "ineffective
// dynamic import" notice). That is an accepted trade-off — the articles are
// small and the metadata must be available synchronously for the index. A
// future optimization could move frontmatter to a build-time sidecar to restore
// per-article code-splitting.
//
// Drafts render in dev + by direct URL but are filtered out of the prod index.

import type { ComponentType } from 'react';
import type { MDXComponents } from 'mdx/types';
import type { BlogCategory, BlogFrontmatter, BlogMeta } from './types.js';

export type MDXModule = {
  default: ComponentType<{ components?: MDXComponents }>;
  frontmatter: BlogFrontmatter;
};

const frontmatters = import.meta.glob<BlogFrontmatter>('../../content/blog/*.mdx', {
  eager: true,
  import: 'frontmatter',
});

const loaders = import.meta.glob<MDXModule>('../../content/blog/*.mdx');

const SHOW_DRAFTS = import.meta.env.DEV;

function slugFromPath(path: string): string {
  return (path.split('/').pop() ?? path).replace(/\.mdx$/, '');
}

const posts: BlogMeta[] = Object.entries(frontmatters)
  .filter((entry): entry is [string, BlogFrontmatter] => Boolean(entry[1]))
  .map(([path, fm]) => ({ ...fm, slug: fm.slug || slugFromPath(path) }))
  .sort((a, b) => (a.date < b.date ? 1 : -1));

const metaBySlug = new Map<string, BlogMeta>(posts.map((p) => [p.slug, p]));

const loaderBySlug = new Map<string, () => Promise<MDXModule>>();
for (const [path, loader] of Object.entries(loaders)) {
  loaderBySlug.set(slugFromPath(path), loader);
}

const isVisible = (p: BlogMeta): boolean => SHOW_DRAFTS || !p.draft;

export function getAllPosts(): BlogMeta[] {
  return posts;
}

export function getPublishedPosts(): BlogMeta[] {
  return posts.filter(isVisible);
}

export function getByCategory(category: BlogCategory): BlogMeta[] {
  return getPublishedPosts().filter((p) => p.category === category);
}

export function getFeatured(): BlogMeta[] {
  return getPublishedPosts().filter((p) => p.featured);
}

export function getPostMeta(slug: string | undefined): BlogMeta | undefined {
  if (!slug) return undefined;
  const meta = metaBySlug.get(slug);
  if (!meta || !isVisible(meta)) return undefined;
  return meta;
}

export function loadPostComponent(slug: string): (() => Promise<MDXModule>) | undefined {
  return loaderBySlug.get(slug);
}

export function getRelated(slug: string, limit = 3): BlogMeta[] {
  const meta = metaBySlug.get(slug);
  if (!meta) return [];

  const explicit = (meta.relatedSlugs ?? [])
    .map((s) => metaBySlug.get(s))
    .filter((m): m is BlogMeta => m !== undefined && isVisible(m));

  if (explicit.length >= limit) return explicit.slice(0, limit);

  const sameCategory = getPublishedPosts().filter(
    (p) => p.category === meta.category && p.slug !== slug && !explicit.includes(p),
  );
  return [...explicit, ...sameCategory].slice(0, limit);
}
